import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { ArrowPathIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import DateInput from '@/components/DateInput'
import { HoverHelp } from '@/tableControls'
import { toast } from '@/store/toastStore'
import { getSettings, updateSetting } from '@/services/settings'

const cardClass =
  'rounded-xl border border-gray-200 bg-white p-4 shadow-sm shadow-gray-950/[0.03]'

type ColumnDef<Row extends Record<string, string>> = {
  key: keyof Row & string
  header: string
  mono?: boolean
}

function rowsEqual<T>(a: T[], b: T[]): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

function parseJsonSetting<T>(
  raw: string | undefined,
  fallback: T,
  label: string
): T {
  if (!raw?.trim()) return fallback
  try {
    return JSON.parse(raw) as T
  } catch {
    toast.warning(`Could not parse ${label}; showing an empty table.`)
    return fallback
  }
}

function MappingTable<Row extends Record<string, string>>({
  columns,
  rows,
  onChange,
  onRemove,
  onAdd,
}: {
  columns: ColumnDef<Row>[]
  rows: Row[]
  onChange: (index: number, key: keyof Row & string, value: string) => void
  onRemove: (index: number) => void
  onAdd: () => void
}) {
  return (
    <div className="space-y-2">
      <div className="table-scroll-mobile overflow-x-auto rounded-lg border border-gray-200">
        <table className="table-forecast min-w-full">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.key}>{col.header}</th>
              ))}
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + 1}
                  className="py-4 text-center text-xs text-gray-500"
                >
                  No rows yet — add one below.
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr key={index} className="[&>td]:align-middle">
                  {columns.map((col) => (
                    <td key={col.key}>
                      <input
                        className={`input w-full min-w-[8rem] text-sm ${
                          col.mono ? 'font-mono' : ''
                        }`}
                        value={row[col.key]}
                        onChange={(e) => onChange(index, col.key, e.target.value)}
                      />
                    </td>
                  ))}
                  <td>
                    <button
                      type="button"
                      className="btn btn-ghost p-1 text-gray-500 hover:text-red-600"
                      aria-label="Remove row"
                      onClick={() => onRemove(index)}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <button type="button" className="btn btn-secondary text-sm" onClick={onAdd}>
        <PlusIcon className="h-4 w-4" />
        Add row
      </button>
    </div>
  )
}

function MappingSubsection({
  title,
  titleHelp,
  titleHelpAriaLabel,
  description,
  dirty,
  saving,
  onSave,
  children,
}: {
  title: string
  titleHelp: ReactNode
  titleHelpAriaLabel: string
  description?: string
  dirty: boolean
  saving: boolean
  onSave: () => void
  children: ReactNode
}) {
  return (
    <div className="border-t border-gray-100 pt-6 first:border-t-0 first:pt-0">
      <div className="mb-3 flex items-start justify-between gap-2">
        <div>
          <h4 className="text-xs font-semibold text-gray-900">
            <HoverHelp label={title} aria-label={titleHelpAriaLabel}>
              {titleHelp}
            </HoverHelp>
          </h4>
          {description ? (
            <p className="mt-1 text-xs text-gray-500">{description}</p>
          ) : null}
        </div>
        {dirty ? <span className="shrink-0 text-xs text-amber-600">Unsaved</span> : null}
      </div>
      {children}
      <div className="mt-3">
        <button
          type="button"
          className="btn btn-primary text-sm"
          onClick={onSave}
          disabled={!dirty || saving}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  )
}

type StoreRow = { storeName: string; merchantId: string; abbreviation: string }
type TenderRow = { merchantId: string; tenderType: string; tenderId: string }
type KnownCustomerRow = {
  merchantId: string
  customerId: string
  classification: string
}
type ExpectedAmountRow = { classification: string; amount: string }

const EMPTY_EXPECTED_AMOUNTS: Record<string, number[]> = {
  cheniere: [],
  fuller_realty: [],
}
type LocationToQbStoreRow = { homebaseLocation: string; qbStore: string }
type EmployeeOverrideRow = { firstName: string; lastName: string; location: string }
type ChildSupportRow = { deductionLabel: string; employeeName: string }

function storeRowsFromSettings(
  merchantIds: Record<string, string>,
  abbreviations: Record<string, string>
): StoreRow[] {
  const names = new Set([...Object.keys(merchantIds), ...Object.keys(abbreviations)])
  return [...names].map((name) => ({
    storeName: name,
    merchantId: merchantIds[name] ?? '',
    abbreviation: abbreviations[name] ?? '',
  }))
}

function tenderRowsFromNested(
  nested: Record<string, Record<string, string>>
): TenderRow[] {
  const rows: TenderRow[] = []
  for (const [merchantId, tenders] of Object.entries(nested)) {
    for (const [tenderType, tenderId] of Object.entries(tenders)) {
      rows.push({ merchantId, tenderType, tenderId })
    }
  }
  return rows
}

function knownCustomerRowsFromNested(
  nested: Record<string, Record<string, string>>
): KnownCustomerRow[] {
  const rows: KnownCustomerRow[] = []
  for (const [merchantId, customers] of Object.entries(nested)) {
    for (const [customerId, classification] of Object.entries(customers)) {
      rows.push({ merchantId, customerId, classification })
    }
  }
  return rows
}

function knownCustomerRowsToNested(
  rows: KnownCustomerRow[]
): Record<string, Record<string, string>> {
  const nested: Record<string, Record<string, string>> = {}
  for (const row of rows) {
    const merchantId = row.merchantId.trim()
    const customerId = row.customerId.trim()
    const classification = row.classification.trim()
    if (!merchantId || !customerId || !classification) continue
    if (!nested[merchantId]) nested[merchantId] = {}
    nested[merchantId][customerId] = classification
  }
  return nested
}

function expectedAmountRowsFromRecord(
  record: Record<string, number[]>
): ExpectedAmountRow[] {
  const rows: ExpectedAmountRow[] = []
  for (const [classification, amounts] of Object.entries(record)) {
    for (const amount of amounts) {
      rows.push({ classification, amount: String(amount) })
    }
  }
  return rows
}

function expectedAmountRowsToRecord(
  rows: ExpectedAmountRow[]
): Record<string, number[]> {
  const record: Record<string, number[]> = {}
  for (const row of rows) {
    const classification = row.classification.trim()
    const amount = Number(row.amount.trim())
    if (!classification || Number.isNaN(amount)) continue
    if (!record[classification]) record[classification] = []
    record[classification].push(amount)
  }
  return record
}

function tenderRowsToNested(
  rows: TenderRow[]
): Record<string, Record<string, string>> {
  const nested: Record<string, Record<string, string>> = {}
  for (const row of rows) {
    const merchantId = row.merchantId.trim()
    const tenderType = row.tenderType.trim()
    const tenderId = row.tenderId.trim()
    if (!merchantId || !tenderType || !tenderId) continue
    if (!nested[merchantId]) nested[merchantId] = {}
    nested[merchantId][tenderType] = tenderId
  }
  return nested
}

function keyValueRowsFromRecord(
  record: Record<string, string>,
  leftKey: string,
  rightKey: string
): Record<string, string>[] {
  return Object.entries(record).map(([left, right]) => ({
    [leftKey]: left,
    [rightKey]: right,
  }))
}

function recordFromKeyValueRows(
  rows: Record<string, string>[],
  leftKey: string,
  rightKey: string
): Record<string, string> {
  const record: Record<string, string> = {}
  for (const row of rows) {
    const left = row[leftKey]?.trim() ?? ''
    const right = row[rightKey]?.trim() ?? ''
    if (!left || !right) continue
    record[left] = right
  }
  return record
}

export function CloverMappingsCard({
  onStoresSaved,
}: {
  onStoresSaved?: () => void
}) {
  const [loading, setLoading] = useState(true)
  const [storeRows, setStoreRows] = useState<StoreRow[]>([])
  const [savedStoreRows, setSavedStoreRows] = useState<StoreRow[]>([])
  const [tenderRows, setTenderRows] = useState<TenderRow[]>([])
  const [savedTenderRows, setSavedTenderRows] = useState<TenderRow[]>([])
  const [knownCustomerRows, setKnownCustomerRows] = useState<KnownCustomerRow[]>([])
  const [savedKnownCustomerRows, setSavedKnownCustomerRows] = useState<KnownCustomerRow[]>(
    []
  )
  const [expectedAmountRows, setExpectedAmountRows] = useState<ExpectedAmountRow[]>([])
  const [savedExpectedAmountRows, setSavedExpectedAmountRows] = useState<
    ExpectedAmountRow[]
  >([])
  const [savingStores, setSavingStores] = useState(false)
  const [savingTenders, setSavingTenders] = useState(false)
  const [savingKnownCustomers, setSavingKnownCustomers] = useState(false)
  const [savingExpectedAmounts, setSavingExpectedAmounts] = useState(false)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const data = await getSettings()
      const merchantIds = parseJsonSetting<Record<string, string>>(
        data.settings.clover_store_merchant_ids?.value,
        {},
        'store merchant IDs'
      )
      const abbreviations = parseJsonSetting<Record<string, string>>(
        data.settings.clover_store_abbreviations?.value,
        {},
        'store abbreviations'
      )
      const tenders = parseJsonSetting<Record<string, Record<string, string>>>(
        data.settings.clover_tender_ids?.value,
        {},
        'tender IDs'
      )
      const knownCustomers = parseJsonSetting<Record<string, Record<string, string>>>(
        data.settings.clover_known_customer_ids?.value,
        {},
        'known customer IDs'
      )
      const expectedAmounts = parseJsonSetting<Record<string, number[]>>(
        data.settings.clover_customer_expected_amounts?.value,
        EMPTY_EXPECTED_AMOUNTS,
        'expected customer amounts'
      )
      const nextStores = storeRowsFromSettings(merchantIds, abbreviations)
      const nextTenders = tenderRowsFromNested(tenders)
      const nextKnownCustomers = knownCustomerRowsFromNested(knownCustomers)
      const nextExpectedAmounts = expectedAmountRowsFromRecord(expectedAmounts)
      setStoreRows(nextStores)
      setSavedStoreRows(nextStores)
      setTenderRows(nextTenders)
      setSavedTenderRows(nextTenders)
      setKnownCustomerRows(nextKnownCustomers)
      setSavedKnownCustomerRows(nextKnownCustomers)
      setExpectedAmountRows(nextExpectedAmounts)
      setSavedExpectedAmountRows(nextExpectedAmounts)
    } catch (err) {
      toast.error('Failed to load Clover mappings')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const storesDirty = useMemo(
    () => !rowsEqual(storeRows, savedStoreRows),
    [storeRows, savedStoreRows]
  )
  const tendersDirty = useMemo(
    () => !rowsEqual(tenderRows, savedTenderRows),
    [tenderRows, savedTenderRows]
  )
  const knownCustomersDirty = useMemo(
    () => !rowsEqual(knownCustomerRows, savedKnownCustomerRows),
    [knownCustomerRows, savedKnownCustomerRows]
  )
  const expectedAmountsDirty = useMemo(
    () => !rowsEqual(expectedAmountRows, savedExpectedAmountRows),
    [expectedAmountRows, savedExpectedAmountRows]
  )

  async function handleSaveStores() {
    const merchantIds: Record<string, string> = {}
    const abbreviations: Record<string, string> = {}
    for (const row of storeRows) {
      const storeName = row.storeName.trim()
      const merchantId = row.merchantId.trim()
      const abbreviation = row.abbreviation.trim()
      if (storeName && merchantId) merchantIds[storeName] = merchantId
      if (storeName && abbreviation) abbreviations[storeName] = abbreviation
    }
    try {
      setSavingStores(true)
      await Promise.all([
        updateSetting('clover_store_merchant_ids', {
          value: JSON.stringify(merchantIds),
        }),
        updateSetting('clover_store_abbreviations', {
          value: JSON.stringify(abbreviations),
        }),
      ])
      const nextStores = storeRowsFromSettings(merchantIds, abbreviations)
      setStoreRows(nextStores)
      setSavedStoreRows(nextStores)
      toast.success('Store mappings saved')
      onStoresSaved?.()
    } catch (err) {
      toast.error('Failed to save store mappings')
      console.error(err)
    } finally {
      setSavingStores(false)
    }
  }

  async function handleSaveTenders() {
    const nested = tenderRowsToNested(tenderRows)
    try {
      setSavingTenders(true)
      await updateSetting('clover_tender_ids', { value: JSON.stringify(nested) })
      const nextTenders = tenderRowsFromNested(nested)
      setTenderRows(nextTenders)
      setSavedTenderRows(nextTenders)
      toast.success('Tender mappings saved')
    } catch (err) {
      toast.error('Failed to save tender mappings')
      console.error(err)
    } finally {
      setSavingTenders(false)
    }
  }

  async function handleSaveKnownCustomers() {
    const nested = knownCustomerRowsToNested(knownCustomerRows)
    try {
      setSavingKnownCustomers(true)
      await updateSetting('clover_known_customer_ids', {
        value: JSON.stringify(nested),
      })
      const nextRows = knownCustomerRowsFromNested(nested)
      setKnownCustomerRows(nextRows)
      setSavedKnownCustomerRows(nextRows)
      toast.success('Known customer mappings saved')
    } catch (err) {
      toast.error('Failed to save known customer mappings')
      console.error(err)
    } finally {
      setSavingKnownCustomers(false)
    }
  }

  async function handleSaveExpectedAmounts() {
    const record = expectedAmountRowsToRecord(expectedAmountRows)
    try {
      setSavingExpectedAmounts(true)
      await updateSetting('clover_customer_expected_amounts', {
        value: JSON.stringify(record),
      })
      const nextRows = expectedAmountRowsFromRecord(record)
      setExpectedAmountRows(nextRows)
      setSavedExpectedAmountRows(nextRows)
      toast.success('Expected amounts saved')
    } catch (err) {
      toast.error('Failed to save expected amounts')
      console.error(err)
    } finally {
      setSavingExpectedAmounts(false)
    }
  }

  return (
    <div className={cardClass}>
      <h3 className="container-title">Report mappings</h3>
      <p className="mt-1 text-xs leading-relaxed text-gray-500">
        Used when fetching daily reports and building journal entries.
      </p>
      {loading ? (
        <div className="mt-3 flex items-center gap-2 text-sm text-gray-500">
          <ArrowPathIcon className="h-4 w-4 animate-spin" />
          Loading mappings…
        </div>
      ) : (
        <div className="mt-4 space-y-2">
          <MappingSubsection
            title="Stores"
            titleHelpAriaLabel="Stores help"
            description="Bookkeeper store name, Clover merchant ID, and journal-entry abbreviation."
            titleHelp={
              <>
                Maps Bookkeeper store names to Clover merchant IDs and the short codes used in
                journal entry document numbers (e.g. RO, HP). Store names must match the Allocate
                dropdown.
              </>
            }
            dirty={storesDirty}
            saving={savingStores}
            onSave={() => void handleSaveStores()}
          >
            <MappingTable
              columns={[
                { key: 'storeName', header: 'Store name' },
                { key: 'merchantId', header: 'Merchant ID', mono: true },
                { key: 'abbreviation', header: 'Abbreviation' },
              ]}
              rows={storeRows}
              onChange={(index, key, value) =>
                setStoreRows((prev) =>
                  prev.map((row, i) => (i === index ? { ...row, [key]: value } : row))
                )
              }
              onRemove={(index) =>
                setStoreRows((prev) => prev.filter((_, i) => i !== index))
              }
              onAdd={() =>
                setStoreRows((prev) => [
                  ...prev,
                  { storeName: '', merchantId: '', abbreviation: '' },
                ])
              }
            />
          </MappingSubsection>

          <MappingSubsection
            title="Tenders"
            titleHelpAriaLabel="Tenders help"
            description="Tender type IDs for each Clover merchant (Cash, Check, …)."
            titleHelp={
              <>
                Clover tender IDs keyed by merchant ID, then by tender type (Cash, Check, …).
                Merchant IDs should match the Stores table.
              </>
            }
            dirty={tendersDirty}
            saving={savingTenders}
            onSave={() => void handleSaveTenders()}
          >
            <MappingTable
              columns={[
                { key: 'merchantId', header: 'Merchant ID', mono: true },
                { key: 'tenderType', header: 'Tender type' },
                { key: 'tenderId', header: 'Tender ID', mono: true },
              ]}
              rows={tenderRows}
              onChange={(index, key, value) =>
                setTenderRows((prev) =>
                  prev.map((row, i) => (i === index ? { ...row, [key]: value } : row))
                )
              }
              onRemove={(index) =>
                setTenderRows((prev) => prev.filter((_, i) => i !== index))
              }
              onAdd={() =>
                setTenderRows((prev) => [
                  ...prev,
                  { merchantId: '', tenderType: '', tenderId: '' },
                ])
              }
            />
          </MappingSubsection>

          <MappingSubsection
            title="Known Clover customer ids"
            titleHelpAriaLabel="Known customer ids help"
            description="Map Clover customer ids to payment classifications per merchant."
            titleHelp={
              <>
                When a Check/Cash payment includes a known Clover customer id, it is classified
                to Cheniere or Fuller Realty before note heuristics run. Nested by merchant id,
                then customer id.
              </>
            }
            dirty={knownCustomersDirty}
            saving={savingKnownCustomers}
            onSave={() => void handleSaveKnownCustomers()}
          >
            <MappingTable
              columns={[
                { key: 'merchantId', header: 'Merchant ID', mono: true },
                { key: 'customerId', header: 'Customer ID', mono: true },
                { key: 'classification', header: 'Classification' },
              ]}
              rows={knownCustomerRows}
              onChange={(index, key, value) =>
                setKnownCustomerRows((prev) =>
                  prev.map((row, i) => (i === index ? { ...row, [key]: value } : row))
                )
              }
              onRemove={(index) =>
                setKnownCustomerRows((prev) => prev.filter((_, i) => i !== index))
              }
              onAdd={() =>
                setKnownCustomerRows((prev) => [
                  ...prev,
                  { merchantId: '', customerId: '', classification: '' },
                ])
              }
            />
          </MappingSubsection>

          <MappingSubsection
            title="Expected amounts"
            titleHelpAriaLabel="Expected amounts help"
            description="Standing check amounts used to sanity-check Cheniere and Fuller Realty payments."
            titleHelp={
              <>
                Each row is a classification and expected dollar amount. Multiple rows for the
                same classification form a list of standing amounts (e.g. Fuller Realty rents).
              </>
            }
            dirty={expectedAmountsDirty}
            saving={savingExpectedAmounts}
            onSave={() => void handleSaveExpectedAmounts()}
          >
            <MappingTable
              columns={[
                { key: 'classification', header: 'Classification' },
                { key: 'amount', header: 'Amount' },
              ]}
              rows={expectedAmountRows}
              onChange={(index, key, value) =>
                setExpectedAmountRows((prev) =>
                  prev.map((row, i) => (i === index ? { ...row, [key]: value } : row))
                )
              }
              onRemove={(index) =>
                setExpectedAmountRows((prev) => prev.filter((_, i) => i !== index))
              }
              onAdd={() =>
                setExpectedAmountRows((prev) => [
                  ...prev,
                  { classification: '', amount: '' },
                ])
              }
            />
          </MappingSubsection>
        </div>
      )}
    </div>
  )
}

function SettingsTableCard({
  title,
  description,
  loading,
  loadingLabel,
  dirty,
  saving,
  onSave,
  children,
}: {
  title: string
  description: string
  loading: boolean
  loadingLabel: string
  dirty: boolean
  saving: boolean
  onSave: () => void
  children: ReactNode
}) {
  return (
    <div className={cardClass}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="container-title">{title}</h3>
          <p className="mt-1 text-xs leading-relaxed text-gray-500">{description}</p>
        </div>
        {dirty ? <span className="shrink-0 text-xs text-amber-600">Unsaved</span> : null}
      </div>
      {loading ? (
        <div className="mt-3 flex items-center gap-2 text-sm text-gray-500">
          <ArrowPathIcon className="h-4 w-4 animate-spin" />
          {loadingLabel}
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          {children}
          <button
            type="button"
            className="btn btn-primary text-sm"
            onClick={onSave}
            disabled={!dirty || saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
    </div>
  )
}

function parseEmployeeOverrides(raw: string | undefined): EmployeeOverrideRow[] {
  const parsed = parseJsonSetting<unknown>(raw, [], 'employee location overrides')
  if (Array.isArray(parsed)) {
    return parsed.map((row) => ({
      firstName: (row as EmployeeOverrideRow).firstName ?? '',
      lastName: (row as EmployeeOverrideRow).lastName ?? '',
      location: (row as EmployeeOverrideRow).location ?? '',
    }))
  }
  if (raw?.trim()) {
    toast.warning('Could not parse employee location overrides; showing an empty table.')
  }
  return []
}

export function LocationMappingsCard() {
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState<LocationToQbStoreRow[]>([])
  const [savedRows, setSavedRows] = useState<LocationToQbStoreRow[]>([])
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const data = await getSettings()
      const locationToQb = parseJsonSetting<Record<string, string>>(
        data.settings.homebase_payroll_location_to_qb_store?.value,
        {},
        'location to QB store mappings'
      )
      const nextRows = keyValueRowsFromRecord(
        locationToQb,
        'homebaseLocation',
        'qbStore'
      ) as LocationToQbStoreRow[]
      setRows(nextRows)
      setSavedRows(nextRows)
    } catch (err) {
      toast.error('Failed to load location mappings')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const dirty = useMemo(() => !rowsEqual(rows, savedRows), [rows, savedRows])

  async function handleSave() {
    const record = recordFromKeyValueRows(rows, 'homebaseLocation', 'qbStore')
    try {
      setSaving(true)
      await updateSetting('homebase_payroll_location_to_qb_store', {
        value: JSON.stringify(record),
      })
      const nextRows = keyValueRowsFromRecord(
        record,
        'homebaseLocation',
        'qbStore'
      ) as LocationToQbStoreRow[]
      setRows(nextRows)
      setSavedRows(nextRows)
      toast.success('Location mappings saved')
    } catch (err) {
      toast.error('Failed to save location mappings')
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <SettingsTableCard
      title="Location mappings"
      description="Homebase location name → Bookkeeper/QB store name."
      loading={loading}
      loadingLabel="Loading mappings…"
      dirty={dirty}
      saving={saving}
      onSave={() => void handleSave()}
    >
      <MappingTable
        columns={[
          { key: 'homebaseLocation', header: 'Homebase location' },
          { key: 'qbStore', header: 'QB store' },
        ]}
        rows={rows}
        onChange={(index, key, value) =>
          setRows((prev) => prev.map((row, i) => (i === index ? { ...row, [key]: value } : row)))
        }
        onRemove={(index) => setRows((prev) => prev.filter((_, i) => i !== index))}
        onAdd={() => setRows((prev) => [...prev, { homebaseLocation: '', qbStore: '' }])}
      />
    </SettingsTableCard>
  )
}

export function EmployeeLocationOverridesCard() {
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState<EmployeeOverrideRow[]>([])
  const [savedRows, setSavedRows] = useState<EmployeeOverrideRow[]>([])
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const data = await getSettings()
      const nextRows = parseEmployeeOverrides(
        data.settings.homebase_employee_location_overrides?.value
      )
      setRows(nextRows)
      setSavedRows(nextRows)
    } catch (err) {
      toast.error('Failed to load employee location overrides')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const dirty = useMemo(() => !rowsEqual(rows, savedRows), [rows, savedRows])

  async function handleSave() {
    const overrides = rows
      .map((row) => ({
        firstName: row.firstName.trim(),
        lastName: row.lastName.trim(),
        location: row.location.trim(),
      }))
      .filter((row) => row.firstName && row.lastName && row.location)
    try {
      setSaving(true)
      await updateSetting('homebase_employee_location_overrides', {
        value: JSON.stringify(overrides),
      })
      setRows(overrides)
      setSavedRows(overrides)
      toast.success('Employee location overrides saved')
    } catch (err) {
      toast.error('Failed to save employee location overrides')
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <SettingsTableCard
      title="Employee location overrides"
      description="Force specific employees to a location regardless of Homebase assignment."
      loading={loading}
      loadingLabel="Loading overrides…"
      dirty={dirty}
      saving={saving}
      onSave={() => void handleSave()}
    >
      <MappingTable
        columns={[
          { key: 'firstName', header: 'First name' },
          { key: 'lastName', header: 'Last name' },
          { key: 'location', header: 'Location' },
        ]}
        rows={rows}
        onChange={(index, key, value) =>
          setRows((prev) => prev.map((row, i) => (i === index ? { ...row, [key]: value } : row)))
        }
        onRemove={(index) => setRows((prev) => prev.filter((_, i) => i !== index))}
        onAdd={() =>
          setRows((prev) => [...prev, { firstName: '', lastName: '', location: '' }])
        }
      />
    </SettingsTableCard>
  )
}

export function DeductionLabelsCard() {
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState<ChildSupportRow[]>([])
  const [savedRows, setSavedRows] = useState<ChildSupportRow[]>([])
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const data = await getSettings()
      const labels = parseJsonSetting<Record<string, string>>(
        data.settings.homebase_child_support_label_to_employee?.value,
        {},
        'deduction label mappings'
      )
      const nextRows = keyValueRowsFromRecord(
        labels,
        'deductionLabel',
        'employeeName'
      ) as ChildSupportRow[]
      setRows(nextRows)
      setSavedRows(nextRows)
    } catch (err) {
      toast.error('Failed to load deduction labels')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const dirty = useMemo(() => !rowsEqual(rows, savedRows), [rows, savedRows])

  async function handleSave() {
    const record: Record<string, string> = {}
    for (const row of rows) {
      const label = row.deductionLabel.trim().toLowerCase()
      const employeeName = row.employeeName.trim()
      if (!label || !employeeName) continue
      record[label] = employeeName
    }
    try {
      setSaving(true)
      await updateSetting('homebase_child_support_label_to_employee', {
        value: JSON.stringify(record),
      })
      const nextRows = keyValueRowsFromRecord(
        record,
        'deductionLabel',
        'employeeName'
      ) as ChildSupportRow[]
      setRows(nextRows)
      setSavedRows(nextRows)
      toast.success('Deduction labels saved')
    } catch (err) {
      toast.error('Failed to save deduction labels')
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <SettingsTableCard
      title="Deduction labels"
      description="Homebase deduction label → employee name. Labels are stored lowercase."
      loading={loading}
      loadingLabel="Loading labels…"
      dirty={dirty}
      saving={saving}
      onSave={() => void handleSave()}
    >
      <MappingTable
        columns={[
          { key: 'deductionLabel', header: 'Deduction label' },
          { key: 'employeeName', header: 'Employee name' },
        ]}
        rows={rows}
        onChange={(index, key, value) =>
          setRows((prev) => prev.map((row, i) => (i === index ? { ...row, [key]: value } : row)))
        }
        onRemove={(index) => setRows((prev) => prev.filter((_, i) => i !== index))}
        onAdd={() => setRows((prev) => [...prev, { deductionLabel: '', employeeName: '' }])}
      />
    </SettingsTableCard>
  )
}

export function WagesGrossFromCard() {
  const [loading, setLoading] = useState(true)
  const [value, setValue] = useState('')
  const [savedValue, setSavedValue] = useState('')
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const data = await getSettings()
      const next = data.settings.homebase_payroll_wages_gross_from?.value?.trim() ?? ''
      setValue(next)
      setSavedValue(next)
    } catch (err) {
      toast.error('Failed to load wages gross setting')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const dirty = value !== savedValue

  async function handleSave() {
    try {
      setSaving(true)
      await updateSetting('homebase_payroll_wages_gross_from', { value })
      setSavedValue(value)
      toast.success('Wages gross setting saved')
    } catch (err) {
      toast.error('Failed to save wages gross setting')
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className={cardClass}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="container-title">
            <HoverHelp label="Wages gross from" aria-label="Wages gross from help">
              Payday on or after this date: JE debits Payroll – Wages at gross and credits Payroll – Benefits for employee pre-tax deductions. Leave blank to keep today's net wages.
            </HoverHelp>
          </h3>
          <p className="mt-1 text-xs leading-relaxed text-gray-500">
            Switch payroll journal entries to gross wages with a per-store benefits credit.
          </p>
        </div>
        {dirty ? <span className="shrink-0 text-xs text-amber-600">Unsaved</span> : null}
      </div>
      {loading ? (
        <div className="mt-3 flex items-center gap-2 text-sm text-gray-500">
          <ArrowPathIcon className="h-4 w-4 animate-spin" />
          Loading setting…
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-gray-600">Effective payday</label>
            <DateInput
              value={value}
              onChange={setValue}
              dateFormat="iso"
              className="input text-sm"
            />
          </div>
          <button
            type="button"
            className="btn btn-primary text-sm"
            onClick={() => void handleSave()}
            disabled={!dirty || saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
    </div>
  )
}
